// Dashboard-specific color aliases + status tokens (F1.6c).
// Local object to keep the short alias convention (a1, a2, ce, etc.) used
// heavily across Dashboard* files, while also exposing the semaforo tokens
// (statusOk/Warn/Error/Info/Purple/Neutral) that live in lib/design C as the
// canonical source. The hex values below mirror lib/design for parity.
export const C = {
  a1: '#1F3864', // azul1
  a2: '#2F5496', // azul2
  ce: '#33BCE1', // celeste
  ao: '#0E243E', // azulMuyOscuro
  as: '#D6E4F0',
  gc: '#F2F2F2',
  gm: '#D9D9D9',
  go: '#595959',
  w: '#FFF',
  b: '#000',
  tit: '#1F3864',
  verde: '#375623',
  rojo: '#7B0000',
  ambar: '#856404',
  verdeBg: '#E2EFDA',
  rojoBg: '#FCE4D6',
  ambarBg: '#FFF3CD',
  teal: '#1A94BF', // azulClaro
  // Status tokens — mirror de C.* en lib/design para uso interno del modulo Dashboard
  statusOk: '#4CAF50',
  statusWarn: '#FF9800',
  statusError: '#F44336',
  statusInfo: '#1E3A5F',
  statusPurple: '#9C27B0',
  statusNeutral: '#607D8B',
};

export const TIP_COLORS: Record<string, string> = {
  PE: '#2196F3',
  PO: C.statusOk,
  PS: C.statusWarn,
};
export const BAR_COLORS = [C.a1, C.a2, C.ce, C.teal];

export interface DashboardProceso {
  id: string;
  codigo: string;
  nombre: string;
  tipologia: string;
  grupoNumero: number;
  grupoNombre: string;
  fase: string | null;
  representante: string | null;
  direccionResponsable: string | null;
  responsableId: string | null;
  representanteId: string | null;
  direccionId: string | null;
  totalDocumentos: number;
  docsPublicados: number;
  totalFichas: number;
  totalProcedimientos: number;
  totalOtros: number;
  fichasDigital: number;
  procDigital: number;
  otrosDigital: number;
  pctAvance: number;
  entradas: number;
  salidas: number;
  herramientas: number;
  indicadores: number;
  involucrados: number;
  requisitos: number;
  reportes: number;
  indicadoresPorTipo: Record<string, number>;
  rolesInvolucrados: Array<{ rol: string; puestoId: string | null }>;
}

export interface DashboardElement {
  nombre: string;
  codigo: string;
  procesoId: string;
  procesoCodigo: string;
  tipoDocumental: string;
  estado: 'DIGITALIZADO' | 'DOCUMENTADO' | 'PENDIENTE';
}

export interface ProcessDashboardData {
  totalProcesos: number;
  totalDocumentos: number;
  totalFichas: number;
  totalProcedimientos: number;
  totalOtros: number;
  promedioAvance: number;
  procesosCompletos: number;
  procesosSinAvance: number;
  porTipologia: Array<{ tipologia: string; count: number }>;
  porDireccion: Array<{ name: string; count: number }>;
  porGrupo: Array<{ name: string; count: number }>;
  porRepresentante: Array<{ name: string; count: number }>;
  documentation: {
    fichasExist: number;
    fichasDigital: number;
    procExist: number;
    procDigital: number;
    otrosExist: number;
    otrosDigital: number;
    totalProcesses: number;
  };
  procesos: DashboardProceso[];
  elements: DashboardElement[];
  timestamp: string;
}

export type FilterKey =
  | 'tipologia'
  | 'grupoNombre'
  | 'direccion'
  | 'representante'
  | 'rol'
  | 'proceso';
export interface Filters {
  tipologia: Set<string>;
  grupoNombre: Set<string>;
  direccion: Set<string>;
  representante: Set<string>;
  rol: Set<string>;
  proceso: Set<string>;
}

export const emptyFilters = (): Filters => ({
  tipologia: new Set(),
  grupoNombre: new Set(),
  direccion: new Set(),
  representante: new Set(),
  rol: new Set(),
  proceso: new Set(),
});

export const pct = (a: number, b: number): number => (b ? Math.round((a / b) * 100) : 0);
export const semaforo = (v: number) => (v >= 100 ? C.verde : v >= 75 ? C.ambar : C.rojo);

export function abreviar(text: string, max: number = 50): string {
  if (!text) return '';
  const t = text.replace(/_/g, ' ').trim();
  return t.length <= max ? t : t.slice(0, max - 1) + '\u2026';
}

export function abreviarPersona(nombre: string, max: number = 40): string {
  if (!nombre) return '';
  return nombre.length <= max ? nombre : nombre.slice(0, max - 1) + '\u2026';
}

export const FICHA_SECTIONS = [
  'entradas',
  'salidas',
  'herramientas',
  'indicadores',
  'involucrados',
  'requisitos',
  'reportes',
] as const;
export type FichaSection = (typeof FICHA_SECTIONS)[number];

export const fichaPct = (p: DashboardProceso): number => {
  const filled = FICHA_SECTIONS.filter((s) => (p[s] as number) > 0).length;
  return Math.round((filled / 7) * 100);
};

export const calcularMadurez = (procesos: DashboardProceso[]) => {
  const total = procesos.length;
  if (!total)
    return { gobernanza: 0, documentacion: 0, caracterizacion: 0, indicadores: 0, global: 0 };
  const gobernanza =
    (procesos.reduce(
      (a, p) =>
        a + ((p.responsableId ? 1 : 0) + (p.representanteId ? 1 : 0) + (p.direccionId ? 1 : 0)) / 3,
      0,
    ) /
      total) *
    100;
  const documentacion = (procesos.filter((p) => p.totalDocumentos > 0).length / total) * 100;
  const caracterizacion =
    (procesos.reduce((a, p) => {
      const t = FICHA_SECTIONS.filter((s) => (p[s] as number) > 0).length;
      return a + t / 7;
    }, 0) /
      total) *
    100;
  const ind = (procesos.filter((p) => p.indicadores > 0).length / total) * 100;
  const r1 = (n: number) => Math.round(n * 10) / 10;
  return {
    gobernanza: r1(gobernanza),
    documentacion: r1(documentacion),
    caracterizacion: r1(caracterizacion),
    indicadores: r1(ind),
    global: r1((gobernanza + documentacion + caracterizacion + ind) / 4),
  };
};

export const cardStyle: React.CSSProperties = {
  background: C.w,
  border: `0.5px solid ${C.gm}`,
  borderRadius: 6,
  padding: '8px 10px',
  display: 'flex',
  flexDirection: 'column',
  overflow: 'hidden',
  minHeight: 0,
};

export const SecTitle = ({ children }: { children: React.ReactNode }) => (
  <div
    style={{
      fontSize: 10,
      fontWeight: 800,
      color: C.tit,
      textTransform: 'uppercase',
      letterSpacing: 0.6,
      paddingBottom: 2,
      borderBottom: `1.5px solid ${C.tit}`,
      marginBottom: 4,
    }}
  >
    {children}
  </div>
);

export interface BLabelProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  value?: number | string;
}
export const BLabel = ({ x = 0, y = 0, width = 0, height = 0, value }: BLabelProps) => (
  <text x={x + width + 3} y={y + height / 2 + 4} fontSize={11} fill={C.b} fontWeight="800">
    {value}
  </text>
);
